import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC } from 'react';
import styled from 'styled-components';

import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import GoToLink from '@/components/GoToLink';
import ScalableThumbnail from '@/components/ScalableThumbnail';
import { Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { BPTestResource } from '@/types/resources';
import { api } from '@/utils/api';
import { BPTestStatus } from '@/utils/enums';

const ActionsWrap = styled.div`
  z-index: 1;
  position: relative;
`;

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 150px;
`;

const Channel = styled.div`
  display: flex;
  align-items: center;
`;

const ChannelIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: cover;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 50%;
  margin-right: 8px;
`;

const Time = styled.div`
  span {
    color: ${({ theme }) => theme.colors.gray};
    font-size: 9px;
    line-height: 12px;
    margin-top: 6px;
  }
`;

const getStatusLabel = (status: BPTestStatus) => {
  switch (status) {
    case BPTestStatus.PROCESSING:
      return 'Processing';
    case BPTestStatus.ACTIVE:
      return 'Active';
    case BPTestStatus.FAILED:
      return 'Failed';
    case BPTestStatus.ENDED:
      return 'Ended';
  }
};

interface Props {
  areThumbsEnlarged: boolean;
  setAreThumbsEnlarged: React.Dispatch<React.SetStateAction<boolean>>;
  test: BPTestResource;
  projectId: number;
}

const TestsTableRow: FC<Props> = ({
  areThumbsEnlarged,
  setAreThumbsEnlarged,
  test,
  projectId,
}) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const onDelete = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you would like to delete this test?',
      );

      if (!confirmed) {
        return;
      }

      await api.delete(`/v1/editorial/bp-tests/${test.id}`);

      await queryClient.invalidateQueries(['bp_tests', { projectId }]);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>
        {test.thumbnail && (
          <ScalableThumbnail
            areThumbsEnlarged={areThumbsEnlarged}
            setAreThumbsEnlarged={setAreThumbsEnlarged}
            thumbnail={
              test.thumbnail.versions.preview ??
              test.thumbnail.versions.original
            }
          />
        )}
      </Td>
      <Td>{test.name}</Td>
      <Td>{test.message}</Td>
      <Td>
        <a
          href={`https://adsmanager.facebook.com/adsmanager/manage/ads?act=${process.env.REACT_APP_FB_ADS_ACCOUNT}&business_id=1032398303507511&filter_set=SEARCH_BY_CAMPAIGN_GROUP_NAME-STRING%1ECONTAIN%1E%22${test.uuid}%22`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {test.uuid}
        </a>
      </Td>
      <Td>
        <GoToLink onClick={(e) => e.stopPropagation()} link={test.link} />
      </Td>
      <Td>{getStatusLabel(test.status)}</Td>
      <Td>{test.effectiveStatus ?? '-'}</Td>
      <Td>
        <Channel>
          {!!test.channel.thumbnail && (
            <ChannelIcon
              src={test.channel.thumbnail}
              alt={test.channel.name}
              loading="lazy"
            />
          )}
          {test.channel.name}
        </Channel>
      </Td>
      <Td>{test.userName ?? '-'}</Td>
      <Td>{test.budget}</Td>
      <Td>
        {test.statsUpdatedAt ? (
          <Time>
            {DateTime.fromISO(test.statsUpdatedAt).toFormat('dd/MM/y')} ·{' '}
            <span>
              {DateTime.fromISO(test.statsUpdatedAt).toFormat('HH:mm')}
            </span>
          </Time>
        ) : (
          '-'
        )}
      </Td>
      <Td>{test.testDuration ?? '-'}</Td>
      <Td>{test.impressions ?? '-'}</Td>
      <Td>{test.spendings ?? '-'}</Td>
      <Td>{test.clicks ?? '-'}</Td>
      <Td>{test.linkClicks ?? '-'}</Td>
      <Td>{test.ctr ?? '-'}</Td>
      <Td>{test.reactions ?? '-'}</Td>
      <Td>{test.comments ?? '-'}</Td>
      <Td>{test.pageLikes ?? '-'}</Td>
      <Td>{test.currency ?? '-'}</Td>
      <Td>{test.properties.clicks_threshold ?? 100}</Td>
      <Td>{test.properties.target_country ?? 'Worldwide'}</Td>
      <Td>{test.properties.target_device ?? 'All'}</Td>
      <Td>{test.properties.bid_amount ?? '1678'}</Td>
      <Td>
        <Time>
          {DateTime.fromISO(test.createdAt).toFormat('dd/MM/y')} <br />
          <span>{DateTime.fromISO(test.createdAt).toFormat('HH:mm')}</span>
        </Time>
      </Td>
      <Td>
        <ActionsWrap>
          <StyledActionsDropdown>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <TrashIcon />
              Delete
            </ActionsDropdownButton>
          </StyledActionsDropdown>
        </ActionsWrap>
      </Td>
    </tr>
  );
};

export default TestsTableRow;
